import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import moment from 'moment';
import { makeStyles } from 'tss-react/mui';

import { getSettlementTimeline } from '../../../api/paymentsApi/settlementsApi';
import Timeline from '../../../components/Timeline';

import { SettlementTimeline } from '../../../types'
import theme from '../../../theme';

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    maxWidth: '480px',
    paddingTop: '10px',
  },
  accordionRoot: {
    margin: '5px',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: 'Poppins',
    borderRadius: '16px',
    '&.MuiAccordion-root, &.MuiPaper-root': {
      borderRadius: '16px !important',
    },
    '&:first-of-type, &:last-of-type': {
      borderRadius: '16px !important',
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    minHeight: '40px',
    padding: '0 16px',
    fontFamily: 'Poppins',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '16px',
      color: theme.palette.common.white,
      fontFamily: 'Poppins',
    },
    flexDirection: 'row-reverse'
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: '#2e3243',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3), // reduced gap
    fontFamily: 'Poppins',
  },
  externalPaymentBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: 'transparent',
    border: '1px solid #FFFFFF',
    borderRadius: '12px',
    padding: '16px',
    margin: '13px 0 13px 20px',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Inter',
    color: '#FFFFFF',
    marginBottom: '4px',
  },
  description: {
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: 'Inter',
    color: '#FFFFFF',
  },
  accordionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingLeft: '8px',
  },
}));

const SettlementsTimeline = () => {
  const { classes } = useStyles();
  const { settlementId } = useParams<string>();
  const [settlements, setSettlements] = useState<SettlementTimeline[]>([]);

  useEffect(() => {
    if (!settlementId) {
      return;
    }

    getSettlementTimeline(Number(settlementId)).then((res) => {
      if (res.data) {
        setSettlements(res.data);
      }
    });
  }, [settlementId]);

  const formatDate = (dateString: string) => {
    return moment(dateString).format('MMM D, YYYY');
  };

  return (
    <Box className={classes.container}>
      {settlements.map((settlement) => (
        <Accordion
          key={settlement.sequenceNumber}
          // defaultExpanded={idx === 0}
          className={classes.accordionRoot}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{
              color: 'white', background: '#555969',
              borderRadius: '8px',
            }} />}
            className={classes.accordionSummary}
          >
            <Typography className={classes.accordionTitle}>
              {`Wave ${settlement.sequenceNumber}`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            {settlement.events.map((event, eventIdx) =>
              event.status === 'PlaintiffPayment' && event.paymentIntention === 'EXTERNAL' ? (
                <Box className={classes.externalPaymentBox} key={eventIdx}>
                  <Box className={classes.iconContainer}>
                    <InfoOutlinedIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
                  </Box>
                  <Box>
                    <Typography className={classes.title}>{event.statusReadable}</Typography>
                    <Typography className={classes.description}>
                      Payment was already settled outside the Vlaw platform on {formatDate(event.date)}
                    </Typography>
                  </Box>
                </Box>
              ) : null
            )}
            <Timeline
              data={settlement.events
                .filter(
                  (event) => !(event.status === 'PlaintiffPayment' && event.paymentIntention === 'EXTERNAL')
                )
                .map((event) => ({ status: event.statusReadable, date: event.date }))}
            />
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default SettlementsTimeline;